import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bell, X, Filter, Check, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BACKEND_URL } from "../config";

const API = `${BACKEND_URL}/api`;

// Función para reproducir sonido de notificación
const playNotificationSound = () => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.value = 800;
  oscillator.type = 'sine';
  
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.5);
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [showRead, setShowRead] = useState(false);
  const previousCountRef = useRef(0);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      const newNotifications = response.data;
      
      // Calcular no leídas
      const newUnreadCount = newNotifications.filter(n => !n.read).length;
      
      // Si hay más notificaciones no leídas que antes, reproducir sonido y animar
      if (newUnreadCount > previousCountRef.current) {
        playNotificationSound();
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 1000);
      }
      
      previousCountRef.current = newUnreadCount;
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
      toast.error("Error al marcar como leída");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { withCredentials: true });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchNotifications(); // Refresh unread count
      toast.success("Notificación eliminada");
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error("Error al eliminar");
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/notifications/clear-all`, { withCredentials: true });
      setNotifications([]);
      setUnreadCount(0);
      toast.success("Todas las notificaciones eliminadas");
    } catch (error) {
      console.error("Error clearing notifications:", error);
      toast.error("Error al limpiar notificaciones");
    }
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Ahora";
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return `Hace ${diffDays}d`;
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case "work_start": return "🟢";
      case "break_start": return "⏸️";
      case "break_end": return "▶️";
      case "work_end": return "🔴";
      case "report_created": return "📋";
      case "task_completed": return "✅";
      case "chatterly_message": return "💬";
      case "chatterly_tip": return "💸";
      case "chatterly_sale": return "💰";
      case "chatterly_sub": return "⭐";
      default: return "🔔";
    }
  };

  // Filtrar notificaciones
  const filteredNotifications = notifications.filter(n => {
    if (!showRead && n.read) return false;
    if (filterType !== "all" && n.type !== filterType) return false;
    if (filterUser !== "all" && n.user_id !== filterUser) return false;
    return true;
  });

  // Obtener lista única de usuarios
  const uniqueUsers = [...new Set(notifications.map(n => ({ id: n.user_id, name: n.user_name })))];

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative ${isAnimating ? 'animate-bounce' : ''}`}
        >
          <Bell className={`h-5 w-5 ${isAnimating ? 'animate-pulse text-yellow-500' : ''}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        className="w-96 max-h-[600px] overflow-hidden flex flex-col" 
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gray-50 dark:bg-gray-800">
          <div>
            <h3 className="font-semibold text-lg">Notificaciones</h3>
            <p className="text-xs text-gray-500">{unreadCount} sin leer</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearAll}
            className="text-red-500 hover:text-red-700"
          >
            Limpiar todas
          </Button>
        </div>

        {/* Filters */}
        <div className="p-3 border-b bg-gray-50 dark:bg-gray-800 space-y-2">
          <div className="flex gap-2 items-center">
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">Filtros:</span>
          </div>
          <div className="flex gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="chatterly_message">💬 Chatterly Msg</SelectItem>
                <SelectItem value="chatterly_tip">💸 Chatterly Tip</SelectItem>
                <SelectItem value="chatterly_sale">💰 Chatterly Venta</SelectItem>
                <SelectItem value="chatterly_sub">⭐ Chatterly Sub</SelectItem>
                <SelectItem value="work_start">Inicio turno</SelectItem>
                <SelectItem value="break_start">Break inicio</SelectItem>
                <SelectItem value="break_end">Break fin</SelectItem>
                <SelectItem value="work_end">Fin turno</SelectItem>
                <SelectItem value="report_created">Reportes</SelectItem>
                <SelectItem value="task_completed">Tareas</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Usuario" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {uniqueUsers.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input 
              type="checkbox" 
              checked={showRead} 
              onChange={(e) => setShowRead(e.target.checked)}
              className="rounded"
            />
            Mostrar leídas
          </label>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-[400px]">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No hay notificaciones</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div 
                key={notification.id}
                className={`p-3 border-b hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                  !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{notification.title}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {getRelativeTime(notification.created_at)}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => markAsRead(notification.id)}
                        title="Marcar como leída"
                      >
                        <Check className="h-4 w-4 text-green-500" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => deleteNotification(notification.id)}
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
